import { useEffect, useState } from "react";
import CashNotificationsSystem from "@/components/admin/CashNotificationSystem";


interface HeaderProps {
  activeTab: string;
  userId?: number; // Make userId optional since we'll get it from auth
}

interface User {
  id: number;
  username: string;
  name: string;
  role: string;
  dashboard?: string;
  last_login?: string | null;
}

// Auth helper functions
const getStoredUser = (): User | null => {
  // Method 1: From localStorage
  const storedUser = localStorage.getItem('user');
  if (storedUser) {
    try {
      return JSON.parse(storedUser);
    } catch (err) {
      console.error('Error parsing stored user:', err);
    }
  }

  // Method 2: From sessionStorage
  const sessionUser = sessionStorage.getItem('user');
  if (sessionUser) {
    try {
      return JSON.parse(sessionUser);
    } catch (err) {
      console.error('Error parsing session user:', err);
    }
  }

  return null;
};

const Header: React.FC<HeaderProps> = ({ activeTab, userId }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadUser = () => {
      try {
        setLoading(true);
        setError(null);

        // Get user data directly from storage
        const userData = getStoredUser();
        
        if (!userData) {
          throw new Error('No user data found. Please log in again.');
        }

        setUser(userData);
      } catch (err) {
        console.error('Error loading user:', err);
        setError(err instanceof Error ? err.message : 'Failed to load user data');
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, [userId]);

  const getPageTitle = (tab: string) => {
    switch (tab) {
      case 'dashboard': return 'Dashboard';
      case 'transports': return 'Transporturi';
      case 'clients': return 'Clienți';
      case 'management': return 'Management';
      case 'settings': return 'Setări';
      default: return 'Dashboard';
    }
  };

  // Handle loading state
  if (loading) {
    return (
      <header className="bg-white shadow-none md:shadow-sm md:border-b md:border-gray-200 px-4 sm:px-6 py-3 sticky top-0 z-40">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              {getPageTitle(activeTab)}
            </h1>
            <p className="text-gray-600 text-sm">Se încarcă...</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="w-10 h-10 bg-gray-300 rounded-full animate-pulse"></div>
          </div>
        </div>
      </header>
    );
  }

  // Handle error state
  if (error) {
    return (
      <header className="bg-white shadow-none md:shadow-sm md:border-b md:border-gray-200 px-4 sm:px-6 py-3 sticky top-0 z-40">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              {getPageTitle(activeTab)}
            </h1>
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className="bg-white shadow-none md:shadow-sm md:border-b md:border-gray-200 px-4 sm:px-6 py-3 sticky top-0 z-40">
      {/* Desktop View */}
      <div className="hidden md:flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            {getPageTitle(activeTab)}
          </h1>
          <p className="text-gray-600 text-sm">
            Bine ați venit, {user?.name || 'Utilizator'}
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <CashNotificationsSystem />
          <div className="text-right">
            <p className="text-sm font-medium text-gray-800">
              {user?.name}
            </p>
            <p className="text-xs text-gray-600">
              {user?.last_login ? (
                `Ultima conectare: ${new Date(user.last_login).toLocaleString('ro-RO', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit'
                })}`
              ) : (
                `Rol: ${user?.role || 'N/A'}`
              )}
            </p>
          </div>
          <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center text-white font-bold">
            {user?.name?.[0]?.toUpperCase() || 'A'}
          </div>
        </div>
      </div>

      {/* Mobile View */}
      <div className="flex md:hidden items-center justify-between">
        <h1 className="text-lg font-semibold text-gray-800">
          {getPageTitle(activeTab)}
        </h1>
        <div className="flex items-center space-x-3">
          <CashNotificationsSystem />
          <span className="text-sm text-gray-600">
            {user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1) || 'Admin'}
          </span>
          <div className="w-9 h-9 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
            {user?.name?.[0]?.toUpperCase() || 'A'}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;